import { useState } from 'react';
import { api } from '../lib/api';
import type { Decision, Rationale, Tag } from '../lib/api';
import { titleCase } from '../lib/format';

export function scoreBand(score: number | null): 'hi' | 'md' | 'lo' {
  if (score === null) return 'lo';
  return score >= 70 ? 'hi' : score >= 45 ? 'md' : 'lo';
}

export function ScoreBadge({ score, title }: { score: number | null; title?: string }) {
  return <span className={`score ${scoreBand(score)}`} title={title}>{score === null ? '—' : Math.round(score)}</span>;
}

// What NTN calls each decision (PRAXIS Customer Profile Dev §2): relevant, monitoring, pursuing, not relevant.
export const DECISION_LABEL: Record<Decision, string> = { pending: 'Undecided', relevant: 'Relevant', watch: 'Monitoring', pursue: 'Pursuing', pass: 'Not relevant' };

// What a client login reads for the same states: "undecided" is NTN's word, "identified" is the client's.
export const CLIENT_DECISION_LABEL: Record<Decision, string> = { ...DECISION_LABEL, pending: 'Identified' };
export function DecisionPill({ decision, client }: { decision: Decision; client?: boolean }) {
  const kind = decision === 'pursue' ? 'ok' : decision === 'watch' ? 'warn' : decision === 'pass' || (client && decision === 'pending') ? 'muted' : 'info';
  return <span className={`pill ${kind}`}>{(client ? CLIENT_DECISION_LABEL : DECISION_LABEL)[decision] ?? titleCase(decision)}</span>;
}

/** NEW / UPDATED flags for a match relative to the client's last review. */
export function MatchFlags({ m }: { m: { is_new?: boolean; is_updated?: boolean } }) {
  return <>{m.is_new && <span className="tag new">NEW</span>}{m.is_updated && <span className="tag updated">UPDATED</span>}</>;
}

/** Explains a fit score in one line: which evidence contributed and how many points each gave. */
export function RationaleLine({ r, tags }: { r: Rationale | null | undefined; tags: Tag[] }) {
  if (!r) return null;
  const parts: string[] = [];
  if (r.tags?.length) parts.push(r.tags.map((t) => `${tags.find((x) => x.id === t.tag_id)?.label ?? t.tag_id} +${t.points}`).join(', '));
  if (r.naics?.match) parts.push(`NAICS ${r.naics.match} +${r.naics.points}`);
  if (r.agency?.matched) parts.push(`agency ${titleCase(r.agency.matched.toLowerCase())} +${r.agency.points}`);
  if (r.set_aside?.status === 'eligible') parts.push(`set-aside eligible +${r.set_aside.points}`);
  if (r.set_aside?.status === 'ineligible') parts.push(`set-aside ${r.set_aside.notice} not held ${r.set_aside.points}`);
  if (r.notice_type?.points) parts.push(`${titleCase(r.notice_type.type)} ${r.notice_type.points > 0 ? '+' : ''}${r.notice_type.points}`);
  if (r.title_pattern?.dla_item) parts.push(`DLA catalogue item ${r.title_pattern.points}`);
  if (r.keywords?.length) parts.push(r.keywords.map((k) => `“${k.phrase}” +${k.points}`).join(', '));
  return <span className="muted sm rationale">{parts.join(' · ') || 'no scoring detail'}</span>;
}

/** Relevant / Monitor / Pursue / Not relevant. Posts the decision and reports back; the caller refreshes what it needs. */
export function DecisionButtons({ matchId, decision, onChanged, disabled }: { matchId: string; decision: Decision; onChanged: (d: Decision) => void; disabled?: boolean }) {
  const [busy, setBusy] = useState(false);
  async function set(d: Decision) {
    if (busy) return;
    setBusy(true);
    try {
      await api(`/api/matches/${matchId}/decision`, { method: 'POST', json: { decision: d } });
      onChanged(d);
    } finally {
      setBusy(false);
    }
  }
  const btn = (d: Decision, label: string) => (
    <button key={d} type="button" className={`decision-btn ${d} ${decision === d ? 'on' : ''}`} disabled={disabled || busy} onClick={(e) => { e.stopPropagation(); set(decision === d ? 'pending' : d); }}>
      {label}
    </button>
  );
  return <span className="decision-btns" onClick={(e) => e.stopPropagation()}>{btn('relevant', 'Relevant')}{btn('watch', 'Monitor')}{btn('pursue', 'Pursue')}{btn('pass', 'Not relevant')}</span>;
}
